import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { guacHttpClient } from "@/lib/http";
import {
  CompanySummary,
  HttpResponse,
  JobInternalConfig,
  JobsResponse,
  UpdateJobInternalConfigRequest,
} from "@/types";

export const useUpdateSetJobInternalConfig = () => {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updateRequest,
    }: {
      id: string;
      updateRequest: UpdateJobInternalConfigRequest;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/job-internal-configs/${id}`;
      const res = await guacHttpClient.patch<
        JobInternalConfig,
        HttpResponse<JobInternalConfig>,
        UpdateJobInternalConfigRequest
      >(path, updateRequest);

      return res.data;
    },
    onSuccess: (updatedData, variables) => {
      const updatedJobInternalConfig = queryClient.setQueryData(
        ["job-internal-configs", currentCompanyId, currentJobId],
        updatedData,
      );

      // Keep the jobs list cache in sync so JobPage reflects changes immediately.
      queryClient.setQueryData(
        ["jobs", currentCompanyId],
        (cachedData: JobsResponse[] | undefined) => {
          if (!cachedData) return cachedData;
          return cachedData.map((jr) =>
            jr.job.id === currentJobId
              ? { ...jr, jobInternalConfig: updatedData }
              : jr,
          );
        },
      );

      // Update companies summary so "Move candidacy" knows whether AI scheduling
      // agent is live or not for all of the jobs
      // (as it shows necessary warnings for staffers to know what's going on)
      if (variables.updateRequest.stafferAgentActive !== undefined) {
        queryClient.setQueryData(
          ["companies-summary"],
          (cachedData: CompanySummary[] | undefined) => {
            if (!cachedData) return cachedData;
            return cachedData.map((c) =>
              c.id === currentCompanyId
                ? {
                    ...c,
                    jobs: c.jobs.map((j) =>
                      j.id === currentJobId
                        ? {
                            ...j,
                            stafferAgentActive: updatedData.stafferAgentActive,
                          }
                        : j,
                    ),
                  }
                : c,
            );
          },
        );
      }

      // Invalidate the evaluations cache so the "Use Existing Evaluations" dropdown stays fresh.
      if (updatedData.evaluations) {
        void queryClient.invalidateQueries({
          queryKey: ["all-evaluations"],
        });
      }

      return updatedJobInternalConfig;
    },
  });
};
